/**
 * Удаление нескольких записей разом.
 *
 * Отдельного серверного метода нет: удаляем тем же DELETE, что и по одной,
 * — так действуют все те же проверки, а отказ по одной записи не мешает
 * остальным. Запросы идут по несколько параллельно: сотня снимков иначе
 * удалялась бы минуту, а все сразу упёрлись бы в лимиты соединений.
 */
import { useState } from "react";
import { Alert, Button, Modal, Space, Typography, message } from "antd";

import { request } from "../lib/http";

const PARALLEL = 4;

export interface Noun {
  one: string;
  few: string;
  many: string;
}

function plural(count: number, noun: Noun): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return noun.one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return noun.few;
  return noun.many;
}

interface Failure {
  id: string;
  reason: string;
}

async function deleteAll(path: string, ids: string[], onProgress: (done: number) => void): Promise<Failure[]> {
  const failures: Failure[] = [];
  let next = 0;
  let done = 0;
  const worker = async () => {
    while (next < ids.length) {
      const id = ids[next++];
      try {
        await request(`${path}/${id}`, { method: "DELETE" });
      } catch (error) {
        failures.push({ id, reason: error instanceof Error ? error.message : "неизвестная ошибка" });
      }
      onProgress(++done);
    }
  };
  await Promise.all(Array.from({ length: Math.min(PARALLEL, ids.length) }, worker));
  return failures;
}

export function BulkDelete({
  path,
  ids,
  noun,
  describe,
  onClear,
  onDone,
}: {
  /** Адрес ресурса без id: /api/v1/admin/photos. */
  path: string;
  ids: string[];
  noun: Noun;
  /** Подпись записи в отчёте об ошибках — по id. */
  describe: (id: string) => string;
  onClear: () => void;
  onDone: () => void;
}) {
  const [progress, setProgress] = useState<number | null>(null);

  if (ids.length === 0) return null;
  const count = `${ids.length} ${plural(ids.length, noun)}`;

  const run = async () => {
    setProgress(0);
    const failures = await deleteAll(path, ids, setProgress);
    setProgress(null);

    const deleted = ids.length - failures.length;
    if (deleted > 0) message.success(`Удалено: ${deleted} ${plural(deleted, noun)}`);
    if (failures.length > 0) {
      Modal.error({
        title: `Не удалось удалить: ${failures.length} ${plural(failures.length, noun)}`,
        width: 560,
        content: (
          <ul style={{ paddingLeft: 18, margin: 0 }}>
            {failures.slice(0, 15).map((failure) => (
              <li key={failure.id}>
                {describe(failure.id)} — {failure.reason}
              </li>
            ))}
            {failures.length > 15 && <li>…и ещё {failures.length - 15}</li>}
          </ul>
        ),
      });
    }
    onDone();
  };

  const confirm = () =>
    Modal.confirm({
      title: `Удалить ${count}?`,
      content: "Удаление необратимо. Записи, которые сервер удалить откажется, останутся — их список покажем после.",
      okText: `Удалить ${count}`,
      okButtonProps: { danger: true },
      cancelText: "Отмена",
      onOk: () => {
        void run();
      },
    });

  return (
    <Alert
      type="warning"
      showIcon={false}
      style={{ marginBottom: 12 }}
      message={
        <Space wrap style={{ width: "100%", justifyContent: "space-between" }}>
          <Typography.Text strong>
            {progress === null ? `Выбрано: ${count}` : `Удаляем… ${progress} из ${ids.length}`}
          </Typography.Text>
          <Space>
            <Button onClick={onClear} disabled={progress !== null}>
              Снять выделение
            </Button>
            <Button danger type="primary" onClick={confirm} loading={progress !== null}>
              Удалить выбранные
            </Button>
          </Space>
        </Space>
      }
    />
  );
}
